import { InboxSort } from 'src/helpers/NotificationPriorityHelpers';
import { mutate } from 'swr';
import type { SWRInfiniteKeyedMutator } from 'swr/infinite';

import { publicConfig } from '@public-config';

import { InboxNotification, Notification } from 'models';
import { InboxQuery } from 'helpers/NotificationHelpers';

import { DEFAULT_INBOX_FILTERS, InboxFilters, state as inboxState } from 'state/inbox';

import type { NotificationCounts } from '@/collections/notifications/endpoints/counts';

import { ApiService } from './ApiService';
import { InternalTrackingService } from './InternalTrackingService';
import { LocalNotificationService } from './LocalNotificationService';
import { PayloadSDK } from './PayloadSDK';

type InboxFilterQuery = InboxQuery & { team?: string | null; company?: string | null; unread?: boolean };

const INBOX_PAGE_SIZE = 20;
const COUNTS_KEY = '/notifications/counts';

// The inbox lists on screen, to refresh or patch them from outside (their SWR keys are internal).
const inboxLists = new Set<SWRInfiniteKeyedMutator<InboxNotification[][]>>();

const toSearchParams = ({ types, team, company, unread }: InboxFilterQuery) => {
    const params = new URLSearchParams();

    if (types?.length) params.set('types', types.join(','));
    if (team) params.set('team', team);
    if (company) params.set('company', company);
    if (unread) params.set('unread', 'true');

    return params;
};

const NotificationService = {
    pageSize: INBOX_PAGE_SIZE,

    countsKey: (team: string | null = null, company: string | null = null) => [COUNTS_KEY, team, company] as const,

    inbox: async ({
        before,
        limit = INBOX_PAGE_SIZE,
        sort,
        ...filter
    }: InboxFilterQuery & {
        before?: Pick<InboxNotification, 'rank' | 'createdAt'>;
        limit?: number;
        sort: InboxSort;
    }) => {
        const params = toSearchParams(filter);

        params.set('limit', String(limit));
        params.set('sort', sort);

        if (before) {
            params.set('before', before.createdAt);
            params.set('beforeRank', String(before.rank));
        }

        return await ApiService.get<InboxNotification[]>(`${publicConfig.urls.api}/notifications/inbox?${params}`);
    },

    counts: async (team: string | null, company: string | null = null) => {
        const params = toSearchParams({ team, company });

        return await ApiService.get<NotificationCounts>(`${publicConfig.urls.api}/notifications/counts?${params}`);
    },

    refreshInbox: async () => {
        await Promise.all([refreshCounts(), ...[...inboxLists].map((mutateList) => mutateList())]);
    },

    markReadLocally: async (notificationId: string, readAt: string) => {
        const markRead = (pages: InboxNotification[][] = []) =>
            pages.map((page) =>
                page.map((notification) =>
                    notification.id === notificationId ? { ...notification, readAt } : notification,
                ),
            );

        await Promise.all([
            refreshCounts(),
            ...[...inboxLists].map((mutateList) => mutateList(markRead, { revalidate: false })),
        ]);
    },

    registerInboxList: (mutateList: SWRInfiniteKeyedMutator<InboxNotification[][]>) => {
        inboxLists.add(mutateList);

        return () => {
            inboxLists.delete(mutateList);
        };
    },

    receiveNotification: (notification: Notification) => {
        LocalNotificationService.showToast({
            // @ts-ignore
            title: notification.app.title,
            // @ts-ignore
            description: notification.app.text,
            // @ts-ignore
            url: notification.app.url,
        });
    },

    openInbox: (filters: Partial<InboxFilters> = {}) => {
        InternalTrackingService.track('notifications.open-inbox');
        inboxState.opened = true;
        NotificationService.setFilters(filters);
    },

    setFilters: (filters: Partial<InboxFilters>) => {
        Object.assign(inboxState.filters, filters);
    },

    resetFilters: () => {
        inboxState.filters = { ...DEFAULT_INBOX_FILTERS };
    },

    closeInbox: () => {
        inboxState.opened = false;
    },

    read: async (notification: Notification) => {
        try {
            await ApiService.post(`${publicConfig.urls.api}/notifications/${notification.id}/read`);
            await NotificationService.markReadLocally(notification.id, new Date().toISOString());
        } catch {
            // This call can fail if a users isn't authenticated
            console.error(`Failed to read notification: ${notification.id}`);
        } finally {
            InternalTrackingService.track('notifications.read', {
                notification,
            });
        }
    },

    archive: async (notification: InboxNotification) => {
        InternalTrackingService.track('notifications.archive', {
            notification,
        });

        const removeArchived = (pages: InboxNotification[][] = []) =>
            pages.map((page) => page.filter((pageNotification) => pageNotification.id !== notification.id));

        await Promise.all([...inboxLists].map((mutateList) => mutateList(removeArchived, { revalidate: false })));

        try {
            await ApiService.post(`${publicConfig.urls.api}/notifications/${notification.id}/delete`);
            await refreshCounts();
        } catch (error) {
            await NotificationService.refreshInbox();

            throw error;
        }

        LocalNotificationService.show({
            message: 'Notification archived',
            actions: [{ label: 'Undo', onClick: () => NotificationService.restore(notification).then() }],
        });
    },

    restore: async (notification: InboxNotification) => {
        InternalTrackingService.track('notifications.restore', {
            notification,
        });

        const params = new URLSearchParams({ unread: String(!notification.readAt) });

        await ApiService.post(`${publicConfig.urls.api}/notifications/${notification.id}/restore?${params}`);
        await NotificationService.refreshInbox();
    },

    readAll: async (filter: InboxFilterQuery = {}) => {
        InternalTrackingService.track('notifications.read-all');

        await ApiService.post(`${publicConfig.urls.api}/notifications/read-all?${toSearchParams(filter)}`);
        await NotificationService.refreshInbox();
    },

    handle: async (notification: Notification) => {
        InternalTrackingService.track('notifications.handled', {
            notification,
        });

        await ApiService.post(`${publicConfig.urls.api}/notifications/${notification.id}/handle`);
    },

    get: async (id: string) => {
        return await PayloadSDK.findByID({
            collection: 'notifications',
            id,
        });
    },
};

const refreshCounts = () => mutate((key) => Array.isArray(key) && key[0] === COUNTS_KEY);

export { NotificationService };
